import { RefObject } from 'react';
import { CropView } from 'react-native-image-crop-tools';
import { CropMode, CropperTabletMobileProps } from '@v2/components/imageHandling/components/Cropper/components/CropperTabletMobile';
import { isAndroid } from '@helpers';
import RNFS from 'react-native-fs';
import { calculateCropDimensions } from '@reducers/v2/imageHandlingV2/thunks';
import { getAngle } from '@v2/components/imageHandling/helpers';

export const convertFileUriToBase64 = async (fileUri: string): Promise<string> => {
  try {
    const base64 = await RNFS.readFile(fileUri, 'base64');
    return `data:image/png;base64,${base64}`;
  } catch (error) {
    console.error('Error converting file to base64:', error);
    return fileUri;
  }
};

export const isCoordinatesChanged = (init: number, end: number, threshold: number = 10) => {
  if (init == null) {
    return false;
  }
  if (Math.abs(init - end) > threshold) {
    return true;
  }
  return false;
};

let lockRotate = false;
export const rotateImageFN = ({ cropRef, angle, setAngle }: { cropRef: RefObject<CropView>; angle: number; setAngle: (angle: number) => void }) => {
  if (!lockRotate) {
    lockRotate = true;
    cropRef.current?.rotateImage(true);
    if (angle === 270) {
      setAngle(0);
    } else {
      setAngle(angle + 90);
    }
    setTimeout(() => {
      lockRotate = false;
    }, 1000);
  }
};

export const onCropMobileTablet = async ({
  image,
  mode,
  onSave,
  triggerDiscardChangesPopup,
  triggerShow,
  angle,
  calculations,
  actualImageDimensions,
  coordinateChangeThreshold = 10,
  setShowChooseCropModeModalOpen,
  setPendingCropData,
  isNewImage = false,
}: Pick<CropperTabletMobileProps, 'image' | 'triggerDiscardChangesPopup' | 'triggerShow' | 'onSave' | 'angle'> & {
  calculations: { x: number; y: number; width: number; height: number; uri: string; bottom?: number; right?: number; left?: number; top?: number };
  mode: CropMode;
  actualImageDimensions?: { width: number; height: number } | null;
  coordinateChangeThreshold?: number;
  setShowChooseCropModeModalOpen?: (value: boolean) => void;
  setPendingCropData?: (data: any) => void;
  isNewImage?: boolean;
}) => {
  const {
    x: left,
    y: top,
    width,
    height,
    uri,
    top: androidTop = 0,
    left: androidLeft = 0,
    bottom: bottomAndroid = 0,
    right: androidRight = 0,
  } = calculations;

  // Use actual image dimensions if available, otherwise fall back to original dimensions from image
  const actualWidth = actualImageDimensions?.width || image.widthOriginal;
  const actualHeight = actualImageDimensions?.height || image.heightOriginal;
  const { width: imageWidth = actualWidth, height: imageHeight = actualHeight } = image;

  let d = {
    y: top,
    x: left,
    width,
    height,
  };

  if (isAndroid) {
    // Android passes crop area as left, top, bottom, right
    // We need to transform based on rotation angle
    d = (() => {
      const androidWidth = androidRight - androidLeft;
      const androidHeight = bottomAndroid - androidTop;

      // For rotated images, dimensions swap
      const rotatedWidth = angle === 90 || angle === 270 ? actualHeight : actualWidth;
      const rotatedHeight = angle === 90 || angle === 270 ? actualWidth : actualHeight;

      switch (angle) {
        case 0:
          // No rotation - use coordinates as-is
          return {
            y: androidTop,
            x: androidLeft,
            width: androidWidth,
            height: androidHeight,
          };
        case 90:
          // 90° clockwise rotation
          // The crop area rotates with the image
          // left -> top, top -> right (from rotated width), right -> bottom, bottom -> left
          return {
            y: androidLeft,
            x: rotatedWidth - bottomAndroid,
            width: androidHeight,
            height: androidWidth,
          };
        case 180:
          // 180° rotation - both axes inverted
          return {
            y: rotatedHeight - bottomAndroid,
            x: rotatedWidth - androidRight,
            width: androidWidth,
            height: androidHeight,
          };
        case 270:
          // 270° clockwise (90° counter-clockwise)
          // left -> bottom, top -> left, right -> top, bottom -> right (from rotated height)
          return {
            y: rotatedHeight - androidRight,
            x: androidTop,
            width: androidHeight,
            height: androidWidth,
          };
        default:
          return {
            y: androidTop,
            x: androidLeft,
            width: androidWidth,
            height: androidHeight,
          };
      }
    })();
  }

  if (mode === CropMode.CANCEL) {
    // Calculate what the initial crop should be based on cropSettings
    let expectedX = image.x || 0;
    let expectedY = image.y || 0;
    let expectedWidth = image.width || imageWidth;
    let expectedHeight = image.height || imageHeight;

    // If we have cropSettings, use calculateCropDimensions to get the expected values
    if (image.isCropped && image.cropSettings?.length > 0 && actualImageDimensions) {
      const expectedCrop = calculateCropDimensions({
        width: actualWidth,
        height: actualHeight,
        cropSettings: image.cropSettings,
        pageNumber: image.pageNumber || 1,
        customAngle: angle,
      });
      expectedX = expectedCrop.x;
      expectedY = expectedCrop.y;
      expectedWidth = expectedCrop.width;
      expectedHeight = expectedCrop.height;
    } else if (!image.isCropped && actualImageDimensions) {
      // For non-cropped images, expect the full actual image dimensions
      // But need to consider rotation - when rotated 90 or 270, dimensions swap
      expectedX = 0;
      expectedY = 0;
      if (angle === 90 || angle === 270) {
        expectedWidth = actualHeight;
        expectedHeight = actualWidth;
      } else {
        expectedWidth = actualWidth;
        expectedHeight = actualHeight;
      }
    }

    const prevAngle = getAngle(image);
    const isAngleChanged = prevAngle !== angle;

    // Only check centered crop logic if x and y are truly not set (not just 0)
    if (expectedX == null && expectedY == null && !image.isCropped) {
      const hOffset = actualHeight - d.height;
      const wOffset = actualWidth - d.width;
      if (
        isCoordinatesChanged(hOffset / 2, d.y, coordinateChangeThreshold) ||
        isCoordinatesChanged(wOffset / 2, d.x, coordinateChangeThreshold) ||
        isAngleChanged
      ) {
        return triggerDiscardChangesPopup();
      } else {
        return triggerShow();
      }
    }

    if (
      isCoordinatesChanged(expectedX, d.x, coordinateChangeThreshold) ||
      isCoordinatesChanged(expectedY, d.y, coordinateChangeThreshold) ||
      isCoordinatesChanged(expectedWidth, d.width, coordinateChangeThreshold) ||
      isCoordinatesChanged(expectedHeight, d.height, coordinateChangeThreshold) ||
      isAngleChanged
    ) {
      return triggerDiscardChangesPopup();
    } else {
      return triggerShow();
    }
  } else {
    // Convert Android file URI to base64 if needed
    let finalUri = uri;
    if (isAndroid && uri.startsWith('/data/')) {
      finalUri = await convertFileUriToBase64(uri);
    }

    // Prepare the crop data
    const cropData = { ...image, ...d, croppedImageUri: finalUri, isProcessing: true, angle };

    // If modal handlers are provided, use the modal flow
    if (setShowChooseCropModeModalOpen && setPendingCropData && !isNewImage) {
      setPendingCropData(cropData);
      setShowChooseCropModeModalOpen(true);
    } else {
      // Fallback to direct save for backward compatibility
      onSave(cropData);
    }
    return;
  }
};
